import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSearchParams } from 'react-router-dom';
import Header from 'shared/components/Header';
import { softwareApi } from '../api/systemRegisterApi';
import { formatEnumLabel } from '../constants/enums';
import { apiErrorMessage, unwrapData } from '../utils/responseHelpers';

const boolLabel = (value) => (value ? 'Yes' : 'No');

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value
      .map((entry) => {
        if (entry && typeof entry === 'object') {
          return entry.name || entry.email || entry.comment || JSON.stringify(entry);
        }
        return typeof entry === 'string' ? formatEnumLabel(entry) : String(entry);
      })
      .join(', ');
  }
  if (typeof value === 'boolean') return boolLabel(value);
  if (typeof value === 'string' && value.includes('_')) return formatEnumLabel(value);
  return String(value);
};

const DetailField = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
      {formatValue(value)}
    </Typography>
  </Grid>
);

const Section = ({ title, children }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryMain = theme.palette.primary.main;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2,
        border: `1px solid ${alpha(primaryMain, isDark ? 0.35 : 0.16)}`,
        borderRadius: 2,
        bgcolor: isDark ? theme.palette.background.paper : '#fff',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
        {title}
      </Typography>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </Paper>
  );
};

export const SOFTWARE_RECORD_VIEW_SPECS = {
  'risk-assessments': {
    title: 'Risk Assessment',
    backPath: '/system-register/risk-register',
    mode: 'one',
    detailKey: 'riskAssessment',
    fields: [
      { name: 'knownBias', label: 'Known bias' },
      { name: 'biasDescription', label: 'Bias description' },
      { name: 'usesArtificialIntelligence', label: 'Uses artificial intelligence' },
    ],
  },
  documents: {
    title: 'Documents',
    backPath: '/system-register/documents',
    mode: 'collection',
    detailKey: 'documents',
    itemTitle: (item, index) => item.documentName || `Document ${index + 1}`,
    fields: [
      { name: 'documentName', label: 'Document name' },
      { name: 'deploymentStatus', label: 'Deployment status' },
      { name: 'fileName', label: 'File name' },
      { name: 'location', label: 'Location / URL' },
    ],
  },
  reviews: {
    title: 'Reviews',
    backPath: '/system-register/reviews',
    mode: 'collection',
    detailKey: 'reviews',
    itemTitle: (item, index) =>
      item.reviewedBy || item.lastReviewDate || `Review ${index + 1}`,
    fields: [
      { name: 'lastReviewDate', label: 'Last review date' },
      { name: 'nextReviewDate', label: 'Next review date' },
      { name: 'reviewedBy', label: 'Reviewed by' },
      { name: 'reviewerRoleCommittee', label: 'Reviewer role / committee' },
      { name: 'performanceMonitoring', label: 'Performance monitoring' },
      { name: 'driftMonitoring', label: 'Drift monitoring' },
      { name: 'reviewComments', label: 'Comments' },
    ],
  },
  incidents: {
    title: 'Incidents',
    backPath: '/system-register/incidents',
    mode: 'collection',
    detailKey: 'incidents',
    itemTitle: (item, index) =>
      item.incidentTicket
      || item.incidentDetails?.slice?.(0, 48)
      || item.incidentResponseOwner
      || `Incident ${index + 1}`,
    fields: [
      { name: 'incidentTicket', label: 'Incident Ticket' },
      { name: 'incidentResponseOwner', label: 'Response owner' },
      { name: 'knownIncidents', label: 'Known incidents' },
      { name: 'incidentDetails', label: 'Details' },
      { name: 'escalationPathDefined', label: 'Escalation path defined' },
      { name: 'incidentDate', label: 'Incident date' },
      { name: 'severityLevel', label: 'Severity' },
      { name: 'rootCause', label: 'Root cause' },
      { name: 'resolutionStatus', label: 'Resolution status' },
      { name: 'actionTaken', label: 'Action taken' },
    ],
  },
  'change-managements': {
    title: 'Change Management',
    backPath: '/system-register/change-managements',
    mode: 'collection',
    detailKey: 'changeManagements',
    itemTitle: (item, index) => item.title || `Change ${index + 1}`,
    fields: [
      { name: 'title', label: 'Title' },
      { name: 'description', label: 'Description' },
      { name: 'changeType', label: 'Change type' },
      { name: 'priority', label: 'Priority' },
      { name: 'riskLevel', label: 'Risk level' },
      { name: 'reasonForChange', label: 'Reason for change' },
      { name: 'requestedBy', label: 'Requested by' },
      { name: 'requestedDate', label: 'Requested date' },
      { name: 'currentVersion', label: 'Current version' },
      { name: 'targetVersion', label: 'Target version' },
      { name: 'implementationPlan', label: 'Implementation plan' },
      { name: 'rollbackPlan', label: 'Rollback plan' },
      { name: 'testPlan', label: 'Test plan' },
      { name: 'testResult', label: 'Test result' },
      { name: 'scheduledStart', label: 'Scheduled start' },
      { name: 'scheduledEnd', label: 'Scheduled end' },
      { name: 'actualStart', label: 'Actual start' },
      { name: 'actualEnd', label: 'Actual end' },
      { name: 'downtimeRequired', label: 'Downtime required' },
      { name: 'downtimeDuration', label: 'Downtime duration (minutes)' },
      { name: 'implementationStatus', label: 'Implementation status' },
      { name: 'implementedBy', label: 'Implemented by' },
    ],
  },
  approvals: {
    title: 'Approvals',
    backPath: '/system-register/approvals',
    mode: 'collection',
    detailKey: 'approvals',
    relatedTitle: 'Related registered system',
    itemTitle: (item, index) =>
      item.approverName || item.approverRole || `Approval ${index + 1}`,
    fields: [
      { name: 'procedureLabel', label: 'Procedure' },
      { name: 'subjectLabel', label: 'Related to' },
      { name: 'softwareName', label: 'Registered system' },
      { name: 'assetSummary', label: 'IT asset' },
      { name: 'incidentTicket', label: 'Incident ticket' },
      { name: 'changeTitle', label: 'Change title' },
      { name: 'monitoringReviewArea', label: 'Monitoring / review area' },
      { name: 'approverName', label: 'Approver name' },
      { name: 'jobPosition', label: 'Job position' },
      { name: 'approverRole', label: 'Approver role' },
      { name: 'approverCommittee', label: 'Committee' },
      { name: 'approvalDate', label: 'Approval date' },
      { name: 'approvalStatus', label: 'Status' },
      { name: 'approvalLevel', label: 'Level' },
      { name: 'approvalComments', label: 'Comments' },
      { name: 'signatureConfirmation', label: 'Signature confirmation' },
    ],
  },
  'deployment-statuses': {
    title: 'Deployment Status',
    backPath: '/system-register/deployment-statuses',
    mode: 'collection',
    detailKey: 'deploymentStatuses',
    itemTitle: (item, index) =>
      formatEnumLabel(item.status) || item.status || `Status ${index + 1}`,
    fields: [
      { name: 'status', label: 'Deployment status' },
      { name: 'stageDate', label: 'Stage date' },
      { name: 'current', label: 'Current status' },
      { name: 'decisionStatus', label: 'Decision' },
      {
        name: 'approvals',
        label: 'Approvals',
        format: (approvals) => {
          if (!Array.isArray(approvals) || !approvals.length) return 'None';
          return approvals
            .map((a) => {
              const who = a.name || a.email || 'Approver';
              const decision =
                a.decision === 'DECLINED'
                  ? 'Declined'
                  : a.decision === 'APPROVED'
                    ? 'Approved'
                    : a.decision || '';
              const when = a.approvedAt
                ? String(a.approvedAt).replace('T', ' ').slice(0, 16)
                : '';
              const comment = a.comment ? ` “${a.comment}”` : '';
              return [decision, who, when].filter(Boolean).join(' · ') + comment;
            })
            .join('\n');
        },
      },
    ],
  },
};

const SoftwareRecordDetailsViewPage = ({ sectionKey }) => {
  const [searchParams] = useSearchParams();
  const softwareId = searchParams.get('softwareId');
  const itemId = searchParams.get('id');

  const spec = SOFTWARE_RECORD_VIEW_SPECS[sectionKey];

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!spec) {
        setError('Unknown details page.');
        setLoading(false);
        return;
      }
      if (!softwareId) {
        setError('No software selected.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await softwareApi.get(softwareId);
        const data = unwrapData(response);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) {
          setError(apiErrorMessage(err, `Failed to load ${spec.title.toLowerCase()} details.`));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [softwareId, spec]);

  const records = useMemo(() => {
    if (!detail || !spec) return [];
    if (spec.mode === 'one') {
      const one = detail[spec.detailKey];
      return one ? [one] : [];
    }
    const list = detail[spec.detailKey] ?? [];
    if (!itemId) return list;
    const selected = list.find((item) => String(item.id) === String(itemId));
    if (!selected) return list;
    return [selected, ...list.filter((item) => String(item.id) !== String(itemId))];
  }, [detail, spec, itemId]);

  if (!spec) {
    return (
      <Box m="20px">
        <Typography color="error">Unknown details page.</Typography>
      </Box>
    );
  }

  return (
    <Box m={{ xs: '12px', sm: '20px' }}>
      <Header title={spec.title} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={36} />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : detail ? (
        <>
          <Section title={spec.relatedTitle || 'Related registered system'}>
            <DetailField label="Software name" value={detail.softwareName} />
            <DetailField label="Internal ID" value={detail.internalReference} />
            <DetailField label="Deployment status" value={detail.deploymentStatus} />
            <DetailField label="Primary purpose" value={detail.primaryPurpose} />
          </Section>

          <Section
            title={
              spec.mode === 'one'
                ? spec.title
                : `${spec.title} (${(detail[spec.detailKey] ?? []).length})`
            }
          >
            {records.length === 0 ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  No {spec.title.toLowerCase()} recorded for this system.
                </Typography>
              </Grid>
            ) : (
              records.map((item, index) => (
                <Grid item xs={12} key={item.id ?? index}>
                  <Box
                    sx={{
                      pl: 1.5,
                      borderLeft: 2,
                      borderColor:
                        itemId && String(item.id) === String(itemId)
                          ? 'secondary.main'
                          : 'divider',
                      mb: index < records.length - 1 ? 1.5 : 0,
                    }}
                  >
                    {spec.mode === 'collection' ? (
                      <Typography variant="subtitle2" fontWeight={700} mb={1}>
                        {spec.itemTitle?.(item, index) || `Item ${index + 1}`}
                        {itemId && String(item.id) === String(itemId) ? ' (selected)' : ''}
                      </Typography>
                    ) : null}
                    <Grid container spacing={1.5}>
                      {spec.fields.map((field) => {
                        const raw = field.format
                          ? field.format(item[field.name])
                          : item[field.name];
                        if (
                          spec.detailKey === 'approvals'
                          && [
                            'procedureLabel',
                            'subjectLabel',
                            'softwareName',
                            'assetSummary',
                            'incidentTicket',
                            'changeTitle',
                            'monitoringReviewArea',
                          ].includes(field.name)
                          && formatValue(raw) === '—'
                        ) {
                          return null;
                        }
                        return (
                          <DetailField
                            key={field.name}
                            label={field.label}
                            value={raw}
                          />
                        );
                      })}
                    </Grid>
                  </Box>
                  {index < records.length - 1 ? <Divider sx={{ my: 1.5 }} /> : null}
                </Grid>
              ))
            )}
          </Section>
        </>
      ) : null}
    </Box>
  );
};

export default SoftwareRecordDetailsViewPage;
